import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Loader } from '@huggg/react-components';
import { Huggg } from 'components';
import SegmentRedemption from './Segments/SegmentRedemption';
import VoucherTypes from './Types/Types';
import dictionary from 'config/dictionary';
import styles from './voucher.module.scss';
import { PrepaidLoader } from 'components/ui/PrepaidCard/PrepaidLoader';
import { getRedeemedSegmentImage, getRedeemedSegmentName, shouldUseRedeemedSegmentInformation } from 'utilities/huggg';
import { format, parseISO } from 'date-fns';
import { interpolate } from 'utilities/interpolate';
import { fallbackError } from 'utilities/errorHandling';

const VoucherClaimed = ({
  brand,
  huggg,
  vouchers,
  voucherExpiry,
  redeemedSegment,
  hideProductImage,
  isFormRedemptionType,
  isPin4Type,
  isPrepaidCardType,
}) => {
  const redeemedSegmentImage = shouldUseRedeemedSegmentInformation(huggg) ? getRedeemedSegmentImage(huggg) : null;
  const redeemedSegmentName = shouldUseRedeemedSegmentInformation(huggg) ? getRedeemedSegmentName(huggg) : null;

  const formattedVoucherExpiry = voucherExpiry ? format(parseISO(voucherExpiry), 'do MMM yyyy') : null;
  const shouldShowVoucherExpiry = Boolean(formattedVoucherExpiry) && !isFormRedemptionType && !isPin4Type && !isPrepaidCardType;

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <Huggg
          huggg={huggg}
          hideImage={hideProductImage}
          imageOverride={redeemedSegmentImage}
          titleOverride={redeemedSegmentName}
          hideDescription={Boolean(redeemedSegmentName)}
        />
      </div>
      <VoucherTypes
        huggg={huggg}
        brandName={brand.name}
        vouchers={vouchers}
        redeemedSegment={redeemedSegment}
        data-test-id="voucher-image"
      />
      {shouldShowVoucherExpiry && <p className={styles.voucherExpiry}>{interpolate(dictionary.voucherExpiry,'[expiry_date]', <>{formattedVoucherExpiry}</>)}</p>}
    </>
  );
};

/**
 * Display the QR Code voucher to the user should they wish to redeem their
 * huggg. Will show segments if any are available.
 *
 * @param {Object} props
 */
const Voucher = props => {
  const history = useHistory();

  const {
    brand,
    huggg,
    isGenerating,
    isTestHugggRedeemed,
    onSegmentConfirm,
    redeemedSegment,
    selectedSegment,
    vouchers,
    voucherError,
    voucherExpiry,
    hideProductImage,
    isFormRedemptionType,
    isPin4Type,
    isPrepaidCardType,
    defaultTerritory,
  } = props;

  const hasVouchers = Boolean(vouchers && vouchers.length);

  // If the voucher requires no specific redemption flow, just call claim straight away
  useEffect(() => {
    if (!hasVouchers && selectedSegment && !isFormRedemptionType && !isPin4Type && !isPrepaidCardType) {
      onSegmentConfirm(selectedSegment, { territory: defaultTerritory });
    }
  }, [hasVouchers, selectedSegment, isFormRedemptionType, isPin4Type, isPrepaidCardType, defaultTerritory, onSegmentConfirm])


  const shouldShowHuggg = !['form-redemption', 'prepaid-card'].includes(selectedSegment?.voucherType);

  const getLoader = useCallback(() => {
    return selectedSegment?.voucherType === 'prepaid-card' ? <PrepaidLoader /> : <Loader />;
  }, [selectedSegment]);

  const getError = useCallback(() => {
    if (selectedSegment?.voucherType !== 'prepaid-card') {
      return <div className={styles.error}>
        {fallbackError}
        </div>;
    }
  }, [selectedSegment]);

  if (hasVouchers) {
    return (
      <VoucherClaimed
        huggg={huggg}
        brand={brand}
        vouchers={vouchers}
        voucherExpiry={voucherExpiry}
        isFormRedemptionType={isFormRedemptionType}
        isPin4Type={isPin4Type}
        isPrepaidCardType={isPrepaidCardType}
        redeemedSegment={redeemedSegment}
        hideProductImage={hideProductImage}
        data-test-id="voucher-claimed"
      />
    );
  } else if (isTestHugggRedeemed) {
    history.push(`/test/${huggg.shortcode}/test-redeemed`);
  }

  return (
    <div data-test-id="voucher-segment">

      {shouldShowHuggg && <div style={{ marginBottom: 20 }}>
        <Huggg huggg={huggg} hideImage={hideProductImage} />
      </div>}

      {voucherError && getError()}

      {!isGenerating && huggg.id && (
        <SegmentRedemption
          huggg={huggg}
          selectedSegment={selectedSegment}
          onConfirm={onSegmentConfirm}
          data-test-id="voucher-segment-redemption-component"
          isFormRedemptionType={isFormRedemptionType}
          isPin4Type={isPin4Type}
          isPrepaidCardType={isPrepaidCardType}
          defaultTerritory={defaultTerritory}
        />
      )}

      {isGenerating && getLoader()}
    </div>
  );
};

VoucherClaimed.propTypes = {
  brand: PropTypes.object.isRequired,
  huggg: PropTypes.object.isRequired,
  vouchers: PropTypes.array,
  voucherExpiry: PropTypes.string,
  isFormRedemptionType: PropTypes.bool,
  isPin4Type: PropTypes.bool,
  isPrepaidCardType: PropTypes.bool,
  redeemedSegment: PropTypes.object,
  hideProductImage: PropTypes.bool,
};

Voucher.propTypes = {
  brand: PropTypes.object.isRequired,
  onSegmentConfirm: PropTypes.func.isRequired,
  huggg: PropTypes.object.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  isTestHugggRedeemed: PropTypes.bool.isRequired,
  selectedSegment: PropTypes.object,
  brandLocation: PropTypes.string,
  error: PropTypes.object,
  expiryDate: PropTypes.instanceOf(Date),
  vouchers: PropTypes.array,
  voucherExpiry: PropTypes.string,
  redeemedSegment: PropTypes.object,
  voucherError: PropTypes.object,
  hideProductImage: PropTypes.bool,
};

export default Voucher;
